import React, { useEffect, useState, useRef, useMemo } from 'react';
import styles from '../Patient.module.css';
import PatientDetailDropdownCard from '../../../components/PatientDetailDropdownCard';
import FilterPatientView from '../FilterPatientDetail';
import { EmergencyContactIcon, TranscriptionIcon, EHRIcon, AttachmentIcon } from '../../../components/icons/Icons';
import { useSelector, useDispatch } from 'react-redux';
import { setNewNote, addNote, setNotes, clearNote } from '../Slices/PatientDetailNotesSlice';
import { Button } from 'react-bootstrap';
import { openModal, closeModal } from '../../../components/PerinHealthModelSlice';
import PerinHealthModal from '../../../components/PerinHealthModel';
import { showAlert } from '../../../components/PerinHealthAlertSlice';
import PatientNotesDetails from '../CardDetails/PatientNotesDetails';
import useAxios from "../../../services/AxiosConfig";
import { formatDateString } from '../../../utils/dateUtils';
import { Paginator } from 'primereact/paginator';
import paginatorStyles from '../../../components/PerinHealthGrid.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faTrash } from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const NotesView = () => {

    const signedInUser = useSelector((state) => state.userInfo);
    const dispatch = useDispatch();
    const { newNoteText, notes, meetingId, title, draftId } = useSelector((state) => state.patientDetailNotes);
    const patient = useSelector((state) => state.patient);
    const { searchText, noteType, dateOrder } = useSelector((state) => state.patientDetailFilter);
    const careTeam = useSelector((state) => state.patient.careTeam);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(20);
    const [syncToEHR, setSyncToEHR] = useState(false);

    const [attachments, setAttachments] = useState([]);
    const [updatingAttachments, setUpdatingAttachments] = useState([]);
    const [deletedFiles, setDeletedFiles] = useState([]);
    const [attachmentError, setAttachmentError] = useState('');
    const fileInputRef = useRef(null);

    const editingDraft = draftId !== "";

    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);

    const dateFormat = useSelector((state) => state.userInfo.dateFormat);

    // Quill editor configuration
    const quillModules = {
        toolbar: [
            ['bold', 'italic'],
        ],
    };

    const quillFormats = ['bold', 'italic'];

    // File type and size validation
    const allowedFileTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const maxFiles = 5;

    const sortedNotes = [...notes].filter(item => (
        (item.title.toLowerCase().startsWith(searchText.toLowerCase()) ||
            item.doctor.toLowerCase().includes(searchText.toLowerCase()) || item.notes.toLowerCase().includes(searchText.toLowerCase())) &&
        (item.title.includes(noteType.split(' ')[0]) || noteType === "")
    )).sort((a, b) => {
        const startDateA = new Date(a.updatedAt);
        const startDateB = new Date(b.updatedAt);
        if (dateOrder === "ascending") {
            return startDateA - startDateB;
        } else {
            return startDateB - startDateA;
        }
    });

    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const currentNotes = useMemo(() => {
        return sortedNotes.slice(first, first + rows);
    }, [sortedNotes, first, rows]);

    function getIcon(iconName) {
        switch (iconName) {
            case "EHR Note":
                return <EHRIcon />;
            case "Transcription":
                return <TranscriptionIcon />;
            default:
                return <EmergencyContactIcon />;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return formatDateString(date.toISOString(), dateFormat).replace(/\//g, '-');
    };

    const fetchPatientNotes = async () => {
        try {
            const response = await httpService.get(`patient/${patient.id}/notes`);
            const fetchPromises = response.data.data.map(async (note) => {
                try {
                    // Fetch the note content
                    let textContent = "Not available";
                    const responseData = await fetch(note.noteUrl);
                    const text = await responseData.text();
                    textContent = text;

                    let doctorName = note.physicianId;
                    // Fetch the doctor's name using the physicianId
                   
                    let doctor = careTeam.find((item)=>item.id === Number(note.physicianId))
                    if (doctor) {
                        doctorName = `${doctor.firstName} ${doctor.lastName}`;
                    } else {
                        await httpService.get(`user/${note.physicianId}`).then((responseData) => {
                            doctorName = `${responseData.data.data.firstName} ${responseData.data.data.lastName}`;
                        })
                            .catch((err) => {
                                console.log(err);
                            });
                    }


                    // Return a new object with only the desired properties
                    return {
                        patientId: note.patientId,
                        noteId: note.noteId,
                        title: note.noteUrl.split("/").slice(-2, -1)[0],  // Extract the note type from the URL
                        doctor: doctorName,
                        updatedAt: note.updatedAt,
                        submissionDate: formatDate(note.updatedAt),
                        notes: textContent,
                        isDraft: note.isDraft,
                        attachmentUrls: note.attachmentUrls,
                        wasTransferred: note.wasTransferred,
                        EHRFileDetail: note.EHRFileDetail
                    };
                } catch (error) {
                    console.log(`Failed to fetch content for note ID ${note.noteId}: ${error}`);
                    return null; // Ignore this note if there's an error
                }
            });

            const notesWithContent = (await Promise.all(fetchPromises)).filter(note => note !== null);
            if (notesWithContent.length > 0) {
                dispatch(setNotes(notesWithContent));
            }

        } catch (error) {

        }
    };

    useEffect(() => {
        fetchPatientNotes();
    }, []);

    // Add effect to update dates when dateFormat changes
    useEffect(() => {
        if (notes.length > 0) {
            const updatedNotes = notes.map(note => ({
                ...note,
                submissionDate: formatDate(note.updatedAt)
            }));
            dispatch(setNotes(updatedNotes));
        }
    }, [dateFormat]);

    const handleNote = (submitting) => {
        if (submitting) {
            dispatch(
                openModal({
                    activeModalId: "NotesView",
                    header: "Submit New Note",
                    message: `Add this note to ${patient.firstName} ${patient.lastName}'s history?`,
                    buttons: [
                        { label: 'Submit Note', variant: 'primary', onClickKey: "Submit" },
                        { label: 'Cancel', variant: 'secondary', onClickKey: "Cancel" },
                    ],
                })
            );
        } else {
            if (editingDraft) {
                updateNote(true)
            } else {
                saveNoteToS3(true)
            }
        }
    };

    const updateNote = async (draftMode) => {
        const file = new Blob([newNoteText], { type: 'text/html' });
        const formData = new FormData();
        formData.append('isDraft', draftMode);
        formData.append('noteFile', file, `notes.html`);

        if (attachments.length > 0) {
            for (const attachment of attachments) {
                const blob = new Blob([attachment], { type: attachment.type });
                formData.append('attachments', blob, attachment.name);
            }
        }

        if (deletedFiles.length > 0) {
            formData.append('filesToRemove', deletedFiles);
        }

        try {
            await httpService.put(`/patient/notes/update/${draftId}`, formData)
                .then(response => {
                    return response;
                })
                .catch(err => console.error('Failed to add note:', err));
        } catch (error) {
            console.error('Error adding note:', error);
            throw error;
        }
        setDeletedFiles([]);
        setAttachments([]);
        setUpdatingAttachments([]);
        dispatch(clearNote());
        fetchPatientNotes();
    }

    const saveNoteToS3 = async (draftMode) => {
        try {

            const file = new Blob([newNoteText], { type: 'text/html' });

            const formData = new FormData();
            formData.append('patientId', patient.id);
            formData.append('physicianId', signedInUser.id);
            formData.append('noteType', title || "Doctor Notes");
            formData.append('isDraft', draftMode);


            if (title === "Transcription") {
                formData.append('noteFile', file, `${meetingId}transcript.html`);
                formData.append('meetingId', meetingId);
            }
            else {
                const currentDate = new Date();
                const formattedDate = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
                const formattedTime = `${currentDate.getHours().toString().padStart(2, '0')}-${currentDate.getMinutes().toString().padStart(2, '0')}-${currentDate.getSeconds().toString().padStart(2, '0')}`;

                formData.append('noteFile', file, `${formattedDate}_${formattedTime}_notes.html`);
            }

            if (attachments.length > 0) {
                for (const attachment of attachments) {
                    const blob = new Blob([attachment], { type: attachment.type });
                    formData.append('attachments', blob, attachment.name);
                }
            }

            await httpService.post(`patient/notes`, formData)
                .then(response => {
                    setAttachments([]);
                    return response;
                })
                .catch(err => console.error('Failed to add note:', err));

        } catch (error) {
            console.error('Error adding note:', error);
            throw error;
        }
        dispatch(clearNote());
        fetchPatientNotes();
    };

    const handleModalOptions = (onClickKey) => {
        if (onClickKey === "Submit") {
            dispatch(closeModal());
            if (editingDraft) {
                updateNote(false);
            } else {
                saveNoteToS3(false);
            }
            dispatch(
                showAlert({
                    header: "Success",
                    message: "New patient note added",
                    type: 'success',
                })
            );
        } else {
            dispatch(closeModal());
        }
    };

    const DraftLabel = {
        name: "Draft",
        color: "#686869"
    };

    const validateFile = (file) => {
        // Check file type
        if (!allowedFileTypes.includes(file.type)) {
            return `File type ${file.type} is not allowed. Please upload PDF, DOCX, JPG, or PNG files only.`;
        }

        // Check file size
        if (file.size > maxFileSize) {
            return `File size ${(file.size / (1024 * 1024)).toFixed(2)}MB exceeds the maximum allowed size of 10MB.`;
        }

        return null;
    };

    const handleAttachmentChange = (e) => {
        const files = Array.from(e.target.files);
        setAttachmentError('');

        // Check if adding these files would exceed the limit
        if (attachments.length + files.length > maxFiles) {
            setAttachmentError(`You can only attach up to ${maxFiles} files.`);
            return;
        }

        const validFiles = [];
        const errors = [];

        files.forEach(file => {
            const error = validateFile(file);
            if (error) {
                errors.push(`${file.name}: ${error}`);
            } else {
                validFiles.push(file);
            }
        });

        if (errors.length > 0) {
            setAttachmentError(errors.join('\n'));
        }

        if (validFiles.length > 0) {
            setAttachments((prevAttachments) => [...prevAttachments, ...validFiles]);
        }
    };

    const handleAttachmentIconClick = () => {
        fileInputRef.current.click();
    };

    const removeAttachment = (indexToRemove) => {
        setAttachments(attachments.filter((_, index) => index !== indexToRemove));
        setAttachmentError('');
    };

    const removeUpdatingAttachment = (indexToRemove) => {
        const deletingFile = updatingAttachments[indexToRemove];
        setDeletedFiles([...deletedFiles, deletingFile.attachmentUrl]);
        setUpdatingAttachments(updatingAttachments.filter((_, index) => index !== indexToRemove));
    };

    const cancelEditingDraft = () => {
        setAttachments([]);
        setDeletedFiles([]);
        setUpdatingAttachments([]);
        setAttachmentError('');
        dispatch(clearNote())
    };

    // Get plain text length for character count
    const getPlainTextLength = (htmlContent) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        return tempDiv.textContent || tempDiv.innerText || '';
    };

    const plainTextLength = getPlainTextLength(newNoteText);

    return (
        <div className={styles.ViewContainer}>
            {/* <div className={styles.PatientViewScrollContainer}> */}
            <div className={styles.NewNoteSection}>
                <div className={styles['quill-container']}>
                    <ReactQuill
                        value={newNoteText}
                        onChange={(content) => dispatch(setNewNote(content))}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Add Notes"
                        className={styles['quill-editor']}
                    />
                </div>
                <div style={{ width: "740px", display: "flex", justifyContent: "flex-end", marginTop: "-5px" }}>
                    <span style={{ fontSize: "10px" }}>{`${plainTextLength.length} characters`}</span>
                </div>
                {editingDraft && updatingAttachments.map((file, index) => (
                    <div key={index} className={styles.AttachedFile}>
                        <span>{file.fileName}</span>
                        <Button variant="link" onClick={() => removeUpdatingAttachment(index)}><FontAwesomeIcon icon={faTrash} /></Button>
                    </div>
                ))}
                {attachments.map((file, index) => (
                    <div key={index} className={styles.AttachedFile}>
                        <span>{file.name} ({(file.size / (1024 * 1024)).toFixed(2)}MB)</span>
                        <Button variant="link" onClick={() => removeAttachment(index)}><FontAwesomeIcon icon={faTrash} /></Button>
                    </div>
                ))}
                {attachmentError && (
                    <div className={styles['attachment-error']}>
                        {attachmentError}
                    </div>
                )}
                <div style={{ display: "flex", columnGap: "10px", paddingLeft: "5px" }}>
                    <input type="checkbox" id="ehr" checked={syncToEHR} onChange={()=>setSyncToEHR(!syncToEHR)}></input>
                    <label className="body-2" htmlFor="ehr">Sync to EHR</label>
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                    <Button
                        variant="secondary"
                        className={styles.NewNoteButton}
                        onClick={() => handleNote(true)}
                        disabled={newNoteText === "" || newNoteText === "<p><br></p>"}
                    >
                        Submit Note
                    </Button>
                    <Button
                        variant="secondary"
                        className={styles.SaveDraftButton}
                        onClick={() => handleNote(false)}
                        disabled={newNoteText === "" || newNoteText === "<p><br></p>"}
                    >
                        {editingDraft ? "Save Draft" : "Save as Draft"}
                    </Button>
                    <Button
                        variant="white"
                        onClick={handleAttachmentIconClick}
                        style={{ display: 'flex', alignItems: "center" }}
                        disabled={attachments.length >= maxFiles}
                        title={attachments.length >= maxFiles ? `Maximum ${maxFiles} files allowed` : `Attach files (${attachments.length}/${maxFiles})`}
                    >
                        <AttachmentIcon />
                        {attachments.length > 0 && (
                            <span style={{
                                fontSize: '12px',
                                color: '#686869',
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: '5px'
                            }}>
                                ({attachments.length}/{maxFiles})
                            </span>
                        )}
                    </Button>
                    <input
                        type="file"
                        multiple
                        accept=".pdf,.docx,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        onChange={handleAttachmentChange}
                        ref={fileInputRef}
                    />
                    {editingDraft &&
                        <Button
                            variant="secondary"
                            className={styles.CancelDraftButton}
                            onClick={cancelEditingDraft}
                        >
                            Cancel
                        </Button>
                    }
                </div>
            </div>
            <FilterPatientView title={'Note History'} isSearch={true} isDate={true} isNote={true} />
            <div className={styles.PatientViewScrollContainer}>
            <div style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "10px"
            }}>
                {currentNotes.map((note, index) => {
                    const hasAttachments = note.attachmentUrls.length > 0;
                    return (
                        <div key={index}>
                            <PatientDetailDropdownCard
                                title={note.title}
                                subtitle={note.doctor}
                                subtitle2={note.submissionDate}
                                subtitle3={hasAttachments ? <span><FontAwesomeIcon icon={faPaperclip} />{` (${note.attachmentUrls.length})`}</span> : null}
                                notePreview={note.notes}
                                icon={getIcon(note.title)}
                                label={
                                    note.isDraft ? DraftLabel : null
                                }

                                cardInfo={<PatientNotesDetails data={note} setAttachments={setAttachments} setUpdatingAttachments={setUpdatingAttachments} />}
                                width={'900px'}
                            />
                            {note.wasTransferred}
                        </div>
                    )
                })
                }
            </div>
            <div style={{ display: "flex", alignItems: "center", paddingTop: "10px", justifyContent: "space-between", width: "740px" }}>
                <span
                    style={{
                        fontFamily: 'Roboto',
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '16px',
                        textAlign: 'left',
                        color: '#25282B'
                    }}>
                    Total Number: {currentNotes.length}
                </span>
                <Paginator
                    className={paginatorStyles.customPaginator}
                    first={first}
                    rows={rows}
                    totalRecords={currentNotes.length}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    onPageChange={onPageChange}
                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                />
                {/* </div> */}
            </div>
            <PerinHealthModal
                modalId={"NotesView"}
                handleButtonClick={handleModalOptions}
            />
            </div>
        </div>
    );
}

export default NotesView;